import React, { useState } from 'react';
import { useAuth, clearLocalData } from './AuthContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [mode, setMode] = useState('signin'); // 'signin' | 'forgot'
  const { signIn, sendPasswordReset } = useAuth();

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setMessage(null);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      // Fire-and-forget: never branch on the result, so we can't leak whether
      // the account exists. Any thrown error (network/rate-limit) is swallowed
      // in favor of the same neutral confirmation.
      await sendPasswordReset(email);
    } catch (_) {
      // intentionally ignored — neutral response below
    }
    setLoading(false);
    setMessage("If that account exists, an email is on its way.");
  };

  const handleResetSession = async () => {
    setResetting(true);
    setError(null);
    setMessage(null);
    
    try {
      // Clear all local data
      clearLocalData();
      setMessage('Session data cleared. Please sign in again.');
      // Reload the page to ensure clean state
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setError('Failed to reset session: ' + err.message);
      setResetting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    // Capture redirect BEFORE signIn triggers re-render.
    //
    // __STICKY_REDIRECT__ (2026-09-13) The else branch is the fix. This key used to
    // be SET when ?redirect= was present and never cleared otherwise, so a value
    // left behind by an earlier bounce (billing sends logged-out users here as
    // ?redirect=billing, and App.js's param branch consumed the URL without clearing
    // the key) survived in sessionStorage for the life of the tab. The next time the
    // user came to the board DIRECTLY and signed in, that stale value threw them
    // into billing -- which is exactly the reported "sign in to Fleet Manager, land
    // in billing". A login with no redirect param means no redirect.
    const params = new URLSearchParams(window.location.search);
    const redirectTarget = params.get('redirect');
    if (redirectTarget) {
      sessionStorage.setItem('postLoginRedirect', redirectTarget);
    } else {
      sessionStorage.removeItem('postLoginRedirect');
    }

    const { error } = await signIn({ email, password });

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-primary)',
      padding: '2rem'
    }}>
      <div className="login-box" style={{
        backgroundColor: 'var(--surface-1)',
        padding: '3rem',
        borderRadius: 'var(--radius-xl)',
        width: '100%',
        maxWidth: '450px',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--border-default)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ 
            color: 'var(--text-primary)', 
            fontSize: '2rem', 
            marginBottom: '0.5rem' 
          }}>
            HM Hauling
          </h1>
          <p style={{ color: 'var(--accent-blue)', fontSize: '1.2rem' }}>
            Fleet Command Center
          </p>
        </div>

        <form onSubmit={mode === 'forgot' ? handleForgot : handleLogin}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              color: 'var(--text-secondary)',
              marginBottom: '0.5rem',
              fontSize: '0.9rem'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-input)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              placeholder="your.email@example.com"
            />
          </div>

          {mode === 'signin' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: 'var(--text-secondary)',
                marginBottom: '0.5rem',
                fontSize: '0.9rem'
              }}>
                Password
              </label>
              {/*
                PASSWORD TRANSFORM CONTRACT — the credential is sent to
                signInWithPassword() exactly as typed: no maxLength, no trim(),
                no case/unicode normalization, no encoding. The set-new-password
                form (ResetPassword.js) MUST apply the identical (none)
                transformation, or a password containing leading/trailing
                whitespace could be set but never verify here. Keep the two in
                sync. (Note: Billing-Manager's change path DOES .trim() — see
                KNOWN_ISSUES.md; that cross-app divergence is intentional-for-now.)
              */}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-input)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                placeholder="••••••••"
              />
            </div>
          )}

          {mode === 'forgot' && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Enter your account email and we'll send a link to reset your password.
            </p>
          )}

          {error && (
            <div style={{
              backgroundColor: 'var(--color-danger)',
              color: 'var(--text-primary)',
              padding: '0.75rem',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}>
              {error}
            </div>
          )}

          {message && (
            <div style={{
              backgroundColor: 'var(--color-success)',
              color: 'var(--text-primary)',
              padding: '0.75rem',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: loading ? 'var(--surface-3)' : 'var(--btn-primary-bg)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            {mode === 'forgot'
              ? (loading ? 'Sending...' : 'Send reset link')
              : (loading ? 'Signing in...' : 'Sign In')}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => switchMode(mode === 'forgot' ? 'signin' : 'forgot')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-blue)',
                fontSize: '0.85rem',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              {mode === 'forgot' ? '← Back to sign in' : 'Forgot password?'}
            </button>
          </div>
        </form>

        <div style={{ 
          marginTop: '2rem', 
          textAlign: 'center',
          paddingTop: '1.5rem',
          borderTop: '1px solid var(--border-default)'
        }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Fleet Management System
          </p>
          <button
            type="button"
            onClick={handleResetSession}
            disabled={resetting}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'transparent',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.8rem',
              cursor: resetting ? 'not-allowed' : 'pointer',
              opacity: resetting ? 0.6 : 1
            }}
          >
            {resetting ? 'Resetting...' : 'Having trouble? Reset Session'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
